import React, { Component, ErrorInfo, ReactNode } from 'react';

// Define the props for the ErrorBoundary component
export interface ErrorBoundaryProps {
  FallbackComponent: React.ComponentType<FallbackComponentProps>;
  onError?: (error: Error, errorInfo: ErrorInfo) => void; // Callback for error logging/reporting
  onReset?: () => void; // Callback to reset the error state
  children: ReactNode;
}

// Define the props for the FallbackComponent
export interface FallbackComponentProps {
  error: Error;
  errorInfo: ErrorInfo;
  onReset: () => void; // Pass the reset method to the FallbackComponent
}

// Define the state for the ErrorBoundary component
export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
      super(props);
      this.state = {
        hasError: false,
        error: null,
        errorInfo: null,
      };
    }
  
    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
      // Update state to indicate an error has occurred
      return { hasError: true, error };
    }
  
    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
      // Log the error or send it to an error reporting service
      console.error('ErrorBoundary caught an error:', error, errorInfo);
      this.setState({ errorInfo });
  
      // Call the onError callback if provided
      if (this.props.onError) {
        this.props.onError(error, errorInfo);
      }
    }
  
    // Method to reset the error state
    resetErrorBoundary = (): void => {
      this.setState({ hasError: false, error: null, errorInfo: null });
  
      // Call the onReset callback if provided
      if (this.props.onReset) {
        this.props.onReset();
      }
    };
  
    render(): ReactNode {
      const { hasError, error, errorInfo } = this.state;
      const { FallbackComponent, children } = this.props;
  
      if (hasError) {
        // Render the FallbackComponent if an error is caught
        return FallbackComponent ? (
          <FallbackComponent
            error={error!}
            errorInfo={errorInfo!}
            onReset={this.resetErrorBoundary}
          />
        ) : (
          <div>
            <h1>Something went wrong.</h1>
            <details style={{ whiteSpace: 'pre-wrap' }}>
              {error && error.toString()}
              <br />
              {errorInfo && errorInfo.componentStack}
            </details>
            <button onClick={this.resetErrorBoundary}>Try Again</button>
          </div>
        );
      }
  
      // Render children if there's no error
      return children;
    }
  }
  
export default ErrorBoundary;
